<template>
	<chatbot
		:alerts="alerts"
		:translations="translations"
		:code="code"
		:cardUrl="customerData.cardUrl"
		:prevent-unload="preventUnload"
		:show-chatbot="showChatbot"
		:market="market"
		@close="closeChat"
	/>
</template>

<script>
	import Message from "./chatbotInstructions/Message";
	import Call from "./chatbotInstructions/Call";
	import Select from "./chatbotInstructions/Select";
	import SelectOption from "./chatbotInstructions/SelectOption";
	import Form from "./chatbotInstructions/Form";
	import FormField from "./chatbotInstructions/FormField";
	import Custom from "./chatbotInstructions/Custom";
	import Card from "./chatbotInstructions/Card";
	import FormFieldOption from "./chatbotInstructions/FormFieldOption";
	import Restart from "./chatbotInstructions/Restart";

	export default {
		props: {
			customerData: {
				type: Object,
				required: true,
			},
			countryCode: {
				type: Number,
				required: true,
				default: 1
			},
			market: {
				type: String,
				required: true,
				default: 'monizze'
			},
			canUseNss: {
				type: Boolean,
				required: true,
				default: 1
			},
			translations: {
				type: Object,
				required: true,
			},
			urls: {
				type: Object,
				required: true,
			},
			hasAutoOrder: {
				type: Boolean,
				required: true,
			},
			alerts: {
				type: Object,
				required: true,
			},
			showChatbot: {
				type: Boolean,
				required: true,
			}
		},
		data ()
		{
			const self = this;

			const trans = this.translations.employee_new;
			return {
				preventUnload: false,
				code: {
					/*
					 * Entry point
					 */
					main: [
						new Message(trans.message.main.intro),
						new Message(trans.message.manual.intro)
								.isFromUser(false),
						new Call('get_user_info'),
						new Call('additional_info').if(this.market !== 'monizze'),
						new Call('virtual_email').if( variables => variables.card_type === 'virtual'),
						new Call('pin_email').if(variables => variables.card_type !== 'virtual' && self.customerData.pinNeedEmail),
						new Call('confirm'),
					],
					/*
					 * Get basic user information and check if already exist in our system.
					 */
					get_user_info: [
						new Call(
							function(variables)
							{
								variables.card_type = 'physical';
								variables.virtualCard = false;
								if (self.customerData && self.customerData.virtualCard === 'Y')
								{
									variables.card_type = 'virtual';
									variables.virtualCard = true;
								}
								self.preventUnload = true;
							}
						),
						new Message(trans.message.get_user_info.intro),
						new Form(
							trans.message.select_card_username.submit_label,
							[
								new FormField('firstname', trans.message.get_user_info.fields.firstname).isRequired(),
								new FormField('lastname', trans.message.get_user_info.fields.lastname).isRequired(),
								self.canUseNss ?
									new FormField('niss', trans.message.get_user_info.fields.niss)
										.isRequired()
										.setHelpMessage(trans.message.get_user_info.help.niss) :
									new FormField('birth_date', trans.message.get_user_info.fields.birth_date)
										.isRequired()
										.setAttribute('type', 'date')
										.setHelpMessage(trans.message.get_user_info.help.birth_date)
								,
							]
						).validateWith(
							function(variables)
							{
								variables.niss = self.canUseNss ? variables.niss : null;
								return (new ApiMonizze())
									.postDataFromApi(
										self.urls.validate_user_info,
										{
											firstname: variables.firstname,
											lastname: variables.lastname,
											niss: variables.niss,
											birth_date: variables.birth_date,
										},
										function(data)
										{
											variables.userExist = data.exist;
											variables.isFromCustomer = data.is_from_customer;
											variables.hasActiveCard = data.has_active_card;
										}
									);
							}
						),
						new Custom(
							function(variables)
							{
								Vue.set(variables, 'fullname', variables.firstname + ' ' + variables.lastname);
								Vue.set(variables, 'card_username', variables.fullname);
							}
						),
						new Message(
							function(variables)
							{
								return self.canUseNss ?
									trans.message.get_user_info.response :
									trans.message.get_user_info.response_birthdate.replace('{birth_date}', vue.filters.date(variables.birth_date))
							})
							.isFromUser(),
						new Call(
							function(variables)
							{
								if(variables.userExist)
								{
									if (variables.isFromCustomer)
									{
										return 'user_already_in_customer';
									}
									else if (variables.hasActiveCard)
									{
										return 'has_user_still_card';
									}
									else {
										variables.needNewCard = true;

										return 'new_user_does_not_have_active_card';
									}

								}
								else if(!variables.language)
								{
									return 'get_user_language';
								}
							}
						),
						new Call('select_card_username')
							.if(
								(variables) => variables.fullname.length > 22
									&& (variables.needNewCard === undefined || variables.needNewCard)
							),
						new Call('get_card_type').if(self.customerData && self.customerData.virtualCard === 'O'),
						new Call('show_card').if((variables) => (!variables.userExist || variables.needNewCard) && !variables.virtualCard),
						new Call('delivery').if((variables) => (!variables.userExist || variables.needNewCard || self.customerData.isHomeDelivery) && !variables.virtualCard),
					],
					/*
					 * The user exist, do we create a new card or not ?
					 */
					has_user_still_card: [
						new Message(trans.message.has_user_still_card.intro),
						new Select(
							'needNewCard',
							[
								new SelectOption(false, trans.message.has_user_still_card.options.false.label),
								new SelectOption(true, trans.message.has_user_still_card.options.true.label),
							]
						),
						new Message(
							(variables) => trans.message.has_user_still_card.options[ String(variables.needNewCard) ].response
						).isFromUser(),
					],
					/*
					 * The user exists and belongs to customer already, cancel or change info?
					 */
					user_already_in_customer: [
						new Message(trans.message.user_already_in_customer.intro),
						new Select(
							'userAlreadyInCustomer',
							[
								new SelectOption('get_user_info', trans.message.user_already_in_customer.actions.correct.label),
																new SelectOption('close', trans.message.user_already_in_customer.actions.cancel.label),
							]
						),
						new Call(function(variables)
						{
							return variables.userAlreadyInCustomer;
						}),
					],
					/*
					* The user exists but does not have active cards and does not belong to the customer
					*/
					new_user_does_not_have_active_card: [
						new Message(trans.message.new_user_does_not_have_active_card.intro),
					],
					/*
					 * Ask user language
					 */
					get_user_language: [
						new Message(trans.message.get_user_language.intro),
						new Select(
							'language',
							Object.entries(trans.language[self.market]).map(([value, label]) => new SelectOption(value, label))
						),
						new Custom(
							function(variables)
							{
								variables.languageLabel = trans.language[self.market][variables.language]
							}
						),
						new Message(trans.message.get_user_language.response)
							.isFromUser(),
					],
					/* Ask card type */
					get_card_type: [
						new Message(trans.message.get_card_type.intro),
						new Select(
							'card_type',
							Object.entries(trans.card_type).map(([value, label]) => new SelectOption(value, label))
						),
						new Custom(
							function(variables)
							{
								variables.virtualCard = variables.card_type === 'virtual';
							}
						),
						new Message(
							function (variables)
							{
								var message1 = trans.message.get_card_type.response
								var message2 = variables.virtualCard ? trans.message.get_card_type.virtual : trans.message.get_card_type.physical

								return message1 + message2
							})
							.isFromUser(false),
					],
					/*
					 * Called if the full name is too long to be the card username, so we ask to select a shorter name
					 */
					select_card_username: [
						new Message(trans.message.select_card_username.intro),
						new Card('card_username').isEditable(trans.message.select_card_username.label, trans.message.select_card_username.submit_label),
						new Message(trans.message.select_card_username.response)
							.isFromUser(),
					],
					/*
					 * Show a demo of the future card
					 */
					show_card: [
						new Message(trans.message.show_card.intro),
						new Card('card_username'),
					],
					/*
					 * Select and show delivery address, lots of possibilities so its moved to a method to generate
					 */
					delivery: self.deliveryCode(trans.message.confirm),
					/*
					 * Ask for the user email so we can contact them for there pin code
					 */
					pin_email: [
						new Message(trans.message.pin_email.intro),
						new Form(
							trans.message.select_card_username.submit_label,
							[
								new FormField('email', trans.message.pin_email.email)
									.setAttribute('type', 'email')
									.isRequired(),
							]
						).validateWith(
							function(variables)
							{
								return (new ApiMonizze())
									.postDataFromApi(
										self.urls.validate_email,
										{
											email: variables.email,
										},
										function() {}
									);
							}
						),
						new Message(trans.message.pin_email.response)
							.isFromUser(),
					],
					virtual_email: [
						new Message(trans.message.virtual_email.intro),
						new Form(
							trans.message.select_card_username.submit_label,
							[
								new FormField('email', trans.message.virtual_email.email)
									.setAttribute('type', 'email')
									.isRequired(),
							]
						).validateWith(
							function(variables)
							{
								return (new ApiMonizze())
									.postDataFromApi(
										self.urls.validate_email,
										{
											email: variables.email,
										},
										function() {}
									);
							}
						),
						new Message(trans.message.virtual_email.response)
							.isFromUser(),
					],
					additional_info: [
						new Message(trans.message.additional_info.intro),
						new Form(trans.message.additional_info.submit_label, [
							new FormField('annual_workdays', trans.message.additional_info.annual_workdays_label)
								.setAttribute('type', 'number')
								.isRequired(false),
							new FormField('employer_contribution', trans.message.additional_info.employer_contribution_label)
								.setAttribute('type', 'number')
								.isRequired(false),
							new FormField('emv_voucher_value', trans.message.additional_info.meal_voucher_value_label)
								.setAttribute('type', 'number')
								.isRequired(false),
						])
					],
					/*
					 * Show a review before confirmation and ask for confirmation
					 */
					confirm: [
						new Message(
							function (variables)
							{
								let recap = '<div class="card-order-recap-content"><b>{fullname}</b><br>{niss}';

								if (self.canUseNss !== true)
								{
									recap = '<div class="card-order-recap-content"><b>{fullname}</b><br>' + vue.filters.date(variables.birth_date);
								}
								if (!variables.userExist)
								{
									recap += '<br>{languageLabel}';
								}

								recap += '<br><br>';

								const price = variables.card_type === 'virtual' ? vue.filters.format_money(self.customerData.cardPriceVirtual) : vue.filters.format_money(self.customerData.cardPrice);

								let willCreateCard = (!variables.userExist || variables.needNewCard);
								let willPayCard = willCreateCard && self.customerData.cardPrice !== 0;

								if ( willCreateCard )
								{
									if (!variables.virtualCard)
									{
										recap += '<b>' + trans.message.confirm.send_card + '</b><br>'
											+ self.formatSelectedAddress(null, variables);
									}
								}
								else
								{
									recap += '<b>' + trans.message.confirm.no_card + '</b>';
								}

								recap += '</div>';

								if ( willPayCard )
								{
									recap += '<div class="card-order-recap-card">' + trans.message.confirm.new_card + '<div class="card-order-recap-card-price">' + price + '€</div></div>';
								}

								recap += '</div>';

								let message = '<p>' + trans.message.confirm.check + '</p>'
									+ '<div class="chatbot-message-card card-order-recap">' + recap + '</div>';

								if ( willPayCard )
								{
									message += '<p>' + trans.message.confirm.invoice + '</p>';
								}

								return message;
							}
						),
						new Message(trans.message.confirm.autoOrder).if(self.hasAutoOrder),
						new Select(
							'confirm_action',
							[
								new SelectOption('save', trans.message.confirm.actions.save.label).addClass('is-primary'),
								new SelectOption('correct', trans.message.confirm.actions.correct.label),
								new SelectOption('cancel', trans.message.confirm.actions.cancel.label),
							]
						),
						new Message(
							function(variables)
							{
								return trans.message.confirm.actions[ variables.confirm_action ].response;
							}
						).isFromUser(false),
						new Call((variables) => variables.confirm_action),
						new Call('confirm'),
					],
					/*
					 * The command is confirmed, call the api to act on it
					 */
					save: [
						new Custom(
							function(variables)
							{
								variables.saved = false;
								variables.type = 'manual';

								if ( variables.selectedAddress )
								{
									variables.address_id = self.customerData.deliveryAddresses[variables.selectedAddress].id;
								}

								(new ApiMonizze).postDataFromApi(
									self.urls.log,
									{
										event: 'clientarea-track.order.create.add-employee-confirm',
										action: 'Click',
										label: 'Order-page – Create Order – Confim addition of employee on chatbot',
										page_name: 'CreateOrderPage',
									}, () => {}
								);

								return (new ApiMonizze())
									.postDataFromApi(
										self.urls.save,
										variables,
										function(data, response)
										{
											variables.saved = true;
											variables.user_id = response.data.user_id;
										}
									);
							}
						),
						new Call('saved').if((variables) => variables.saved),
						new Message(trans.message.save.error),
					],
					/*
					 * The user want to correct something, ask what then call it back
					 */
					correct: [
						new Message(trans.message.correct.intro),
						new Select(
							'to_correct',
							[
								new SelectOption('get_user_info', trans.message.correct.options.get_user_info.label),
								new SelectOption('get_user_language', trans.message.correct.options.get_user_language.label)
									.if((variables) => !variables.userExist),
								new SelectOption('virtual_email', trans.message.correct.options.virtual_email.label).if( variables => variables.card_type === 'virtual'),
								new SelectOption('delivery', trans.message.correct.options.delivery.label)
									.if(
										function(variables)
										{
											return variables.needNewCard !== false &&  variables.card_type !== 'virtual'
												&& (self.customerData.isHomeDelivery || self.customerData.deliveryAddresses.length > 1);
										}
									),
								new SelectOption('pin_email', trans.message.correct.options.pin_email.label)
									.if(variables => variables.card_type === 'virtual' && self.customerData.pinNeedEmail),
							]
						),
						new Message((variables) => trans.message.correct.options[variables.to_correct].response)
							.isFromUser(false),
						new Call((variables) => variables.to_correct),
					],
					/*
					 * Everything is done, congratulation
					 */
					saved: [
						new Custom(
							(variables) =>
							{
								this.$emit('saved', variables.user_id);
							}
						),
						new Call(
							function(variables)
							{
								self.preventUnload = false;
							}
						),
						new Message(
							function (variables)
							{
								return '<p>' + trans.message.saved.welcome + ' ' + variables.firstname + '</p>'
									+ '<p>' + trans.message.saved.subtext + '</p>';
							}
						),
						new Message(trans.message.confirm.card_expedition).if((variables) => !variables.userExist || variables.needNewCard),
						new Select(
							'restart',
							[
								new SelectOption(true, trans.message.restart.true).addClass('is-primary'),
								new SelectOption(false, trans.message.restart.false),
							]
						),
						new Call('close').if((variables) => !variables.restart),
						new Custom(
							() => {
								(new ApiMonizze).postDataFromApi(
									self.urls.log,
									{
										event: 'clientarea-track.order.chatbot.add-employee',
										action: 'Click',
										label: 'Order-page – Click on add-employee on chatbot',
										page_name: 'OrderPage',
									}, () => {}
								);
							}
						).if((variables) => variables.restart),
						new Restart('main'),
					],
					/*
					 * Ask for confirmation before closing the modal
					 */
					cancel: [
						new Message(trans.message.cancel.intro),
						new Select(
							'confirm_cancel',
							[
								new SelectOption(true, trans.message.cancel.options.true.label).addClass('is-danger'),
								new SelectOption(false, trans.message.cancel.options.false.label),
							]
						),
						new Message((variables) => trans.message.cancel.options[String(variables.confirm_cancel)].label)
							.isFromUser(false),
						new Call('close').if((variables) => variables.confirm_cancel),
					],
					/*
					 * TODO
					 */
					upload: [
						new Message(trans.message.upload.response).isFromUser(false),
					],
					/*
					 * Close the modal
					 */
					close: [
						new Custom(
							() =>
							{
								this.$emit('close');
							}
						),
					],
				}
			}
		},
		methods: {
			checkNiss(niss, yearOver2000)
			{
				let numbersToCheck = niss.substring(0,9); // first 10 digits
				let checksum = niss.substring(9,11); // last two digits

				if ( yearOver2000 )
				{
					numbersToCheck = '2' + numbersToCheck;
				}

				const modulo = numbersToCheck % 97;
				const checkResult = 97 - modulo;

				return ('' + checkResult).padStart(2, '0') === checksum;
			},
			deliveryCode(trans)
			{
				const self = this;

				if ( this.customerData.isHomeDelivery )
				{
					return [
						new Message(
							function(variables)
							{
								Vue.set(variables, 'address_country', self.countryCode);
								return trans.home.first + ' ' + variables.firstname + '. ' + trans.home.question;
							}
						),
						new Form(
							trans.home.save,
							[
								new FormField('address_country', trans.home.country + ' *')
									.isRequired()
									.isSelect(
										self.customerData.countries
											.map((country) => new FormFieldOption(country.value, country.label))
									),
								new FormField('address_street', trans.home.street + ' *')
									.setWidth('is-four-fifths')
									.isRequired(),
								new FormField('address_number', trans.home.number + ' *')
									.setWidth('is-one-fifth')
									.isRequired(),
								new FormField('address_box', trans.home.box)
									.setWidth('is-one-fifth'),
								new FormField('address_zip', trans.home.zip + ' *')
									.setWidth('is-one-quarter')
									.isRequired(),
								new FormField('address_city', trans.home.city + ' *')
									.setWidth('')
									.isRequired(),
							]
						),
						new Message((variables) => self.formatSelectedAddress(null, variables))
							.isFromUser(),
					];
				}

				if (this.customerData.deliveryAddresses.length > 1)
				{
					return [
						new Message((variables) => 'Where should we deliver ' + variables.firstname + '\'s card?'),
						new Select(
							'selectedAddress',
							self.customerData.deliveryAddresses.map(
								function(address, i)
								{
									var selected = {
										selectedAddress : i,
									}
									return new SelectOption(i, self.formatSelectedAddress(address,selected))
										.isComplex();
								}
							)
						),
						new Message(
							function(variables)
							{
								return 'Deliver it to:<p class="chatbot-message-card">'
									+ self.formatSelectedAddress(null, variables)
									+ '</p>';
							}
						).isFromUser(),
					];
				}

				if (this.customerData.deliveryAddresses.length === 0)
				{
					return [
						new Message(
							function(variables)
							{
								Vue.set(variables, 'address_country', self.countryCode);
								return trans.post.first + ' ' + trans.post.question;
							}
						),
						new Form(
							trans.post.save,
							[
								new FormField('address_country', trans.post.country + ' *')
									.isRequired()
									.isSelect(
										self.customerData.countries
											.map((country) => new FormFieldOption(country.value, country.label))
									),
								new FormField('address_street', trans.post.street + ' *')
									.setWidth('is-four-fifths')
									.isRequired(),
								new FormField('address_number', trans.post.number + ' *')
									.setWidth('is-one-fifth')
									.isRequired(),
								new FormField('address_box', trans.post.box)
									.setWidth('is-one-fifth'),
								new FormField('address_zip', trans.post.zip + ' *')
									.setWidth('is-one-quarter')
									.isRequired(),
								new FormField('address_city', trans.post.city + ' *')
									.setWidth('')
									.isRequired(),
							]
						),
						new Message((variables) => self.formatSelectedAddress(null, variables))
							.isFromUser(),
					];
				}

				return [
					new Custom(
						function(variables)
						{
							variables.selectedAddress = 0;
						}
					),
					new Message(
						function(variables)
						{
							return '<p>' + trans.address + '</p><p class="chatbot-message-card">'
								+ self.formatSelectedAddress(null, variables)
								+ '</p>';
						}
					),
				];
			},
			formatSelectedAddress(address, variables)
			{
				if (!address)
				{
					if ( this.customerData.isHomeDelivery )
					{
						address = {
							site_name: variables.fullname,
							street: variables.address_street,
							street_number: variables.address_number,
							box: variables.address_box,
							postal_code: variables.address_zip,
							city: variables.address_city,
							country: this.getCountryByCode(variables.address_country),
						};
					}
					else
					{
						if (!this.customerData.deliveryAddresses[variables.selectedAddress])
						{
							address = {
								site_name: variables.fullname,
								street: variables.address_street,
								street_number: variables.address_number,
								box: variables.address_box,
								postal_code: variables.address_zip,
								city: variables.address_city,
								country: this.getCountryByCode(variables.address_country),
							};
						}
						else
						{
							address = this.customerData.deliveryAddresses[variables.selectedAddress];
						}
					}
				}

				let message = '';

				// If customer is migrated, address data has not the same variable names
				if (!this.customerData.isHomeDelivery && this.customerData.deliveryAddresses[variables.selectedAddress] && this.customerData.deliveryAddresses[variables.selectedAddress].migrated)
				{
					message = address.street + ' ' + address.number;

					if (address.box)
					{
						message += ' / ' + address.box;
					}

					message += '<br/>' + address.zip_code + ' ' + address.city
						+ '<br/>' + address.country;
				}
				else
				{
					message = address.site_name + '<br/>'
						+ address.street + ' ' + address.street_number;

					if (address.box)
					{
						message += ' / ' + address.box;
					}

					message += '<br/>' + address.postal_code + ' ' + address.city
						+ '<br/>' + address.country;
				}

				return message;
			},
			getCountryByCode(code)
			{
				for (const country of this.customerData.countries)
				{
					if ( country.value == code )
					{
						return country.label;
					}
				}
			},
			closeChat()
			{
				this.$emit('close');
			}
		}
	}
</script>
